import { useSelector } from 'react-redux'
import type { UserRoleName } from 'src/generated'
import { userRoleSelector } from 'src/redux/providers/selectors'

// ----------------------------------------------------------------------
// Who may see peer-to-peer.
//
// Partners, the vehicle applications they submit and the pricing tiers they
// are paid on are Beast's own book of business, not a provider's. The section
// used to open for any provider carrying the `p2pEnabled` flag, and `partners`
// was never scoped to a provider, so one operator's console listed every other
// operator's partners. It is Beast staff's alone now.
//
// `superAdmin` is this console's name for Beast staff and for nobody else:
// `userRoleSelector` returns it for `auth.isAdmin`, and a provider's own users
// carry `admin`, `fleetManager` or `fleetAssistant`.
//
// One predicate, because the section is refused in six places — the two nav
// groups, the routes behind them, the two links into it left on screens a
// provider still uses, and the rental billing card's price label. Six copies of
// a rule is five chances to disagree, and the one that disagrees is the one
// that leaks.
// ----------------------------------------------------------------------

/**
 * The roles the peer-to-peer section admits. Spread into `roles` on a menu
 * entry and `requiredRole` on a route, so both read the same list.
 */
export const P2P_ROLES: readonly UserRoleName[] = ['superAdmin']

/** Whether this viewer may see peer-to-peer at all. */
export const p2pVisibleTo = (role: UserRoleName | null | undefined): boolean =>
  role != null && P2P_ROLES.includes(role)

/**
 * The same question for a component, which has a store but no role in hand.
 *
 * A screen outside the section that links into it asks this rather than
 * repeating the role, so the link disappears with the section instead of
 * surviving it as a press that lands on the 403.
 */
export const useP2pVisible = (): boolean =>
  p2pVisibleTo(useSelector(userRoleSelector))
